﻿import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, Plus, Trash2, Edit2, Users, Shield, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { api } from '../services/api';
import { useLedgerStore } from '../hooks/use-ledger-store';
import { useToast } from '../hooks/use-toast';
import type { GroupMember } from '../types';

interface Group {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  memberCount?: number;
  isPersonal?: number;
}

export interface GroupSwitcherProps {
  currentGroupId?: string;
  onGroupChange?: (groupId: string) => Promise<void>;
  triggerLabel?: string;
  /** Controlled open state (optional) */
  open?: boolean;
  /** Controlled open change handler (optional) */
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (useful when using a custom trigger) */
  hideTrigger?: boolean;
}

export default function GroupSwitcher({ currentGroupId, onGroupChange, triggerLabel = '群組', open, onOpenChange, hideTrigger = false }: GroupSwitcherProps) {
  const { isSystemAdmin } = useLedgerStore();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const isControlled = typeof open === 'boolean' && typeof onOpenChange === 'function';
  const actualOpen = isControlled ? (open as boolean) : isOpen;
  const setActualOpen = (v: boolean) => {
    if (isControlled) onOpenChange?.(v);
    else setIsOpen(v);
  };
  const [groups, setGroups] = useState<Group[]>([]);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupMembersMap, setGroupMembersMap] = useState<Record<string, GroupMember[]>>({});
  const [loadingMembersId, setLoadingMembersId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');

  useEffect(() => {
    if (actualOpen) {
      loadGroups();
    }
  }, [actualOpen, isSystemAdmin]);

  const loadGroups = async () => {
    try {
      setIsLoading(true);
      // 切換群組只顯示使用者自己加入的群組（系統管理員也不例外）
      const response = await api.groups.list();
      setGroups((response?.groups as Group[]) || []);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadGroupMembers = async (groupId: string) => {
    if (groupMembersMap[groupId]) return;
    setLoadingMembersId(groupId);
    try {
      const resp = await api.groups.listMembers(groupId);
      setGroupMembersMap((prev) => ({ ...prev, [groupId]: resp?.members ?? [] }));
    } catch {
      setGroupMembersMap((prev) => ({ ...prev, [groupId]: [] }));
    } finally {
      setLoadingMembersId(null);
    }
  };

  const handleToggleMembers = async (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    await loadGroupMembers(groupId);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const response = await api.groups.create({ name: newGroupName, description: newGroupDesc || null });
      setGroups([...groups, response?.group as Group]);
      setNewGroupName('');
      setNewGroupDesc('');
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleUpdateGroup = async (groupId: string) => {
    try {
      await api.groups.update(groupId, { name: editName, description: editDesc || null });
      setGroups(groups.map((g) => (g.id === groupId ? { ...g, name: editName, description: editDesc } : g)));
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm('確定要刪除此群組嗎？群組成員記錄將移除，支出紀錄保留供歷史查詢。')) return;
    try {
      await api.groups.delete(groupId);
      setGroups(groups.filter((g) => g.id !== groupId));
      if (expandedGroupId === groupId) setExpandedGroupId(null);
    } catch (error: any) {
      const msg = error?.message || '';
      if (msg.includes('unsettled_expenses') || msg.includes('UNSETTLED_EXPENSES')) {
        showToast('群組內尚有未結清帳款，請先完成所有結算再刪除群組。', 'error');
      } else if (msg.includes('cannot_delete_personal_group') || msg.includes('CANNOT_DELETE_PERSONAL_GROUP')) {
        showToast('個人帳本無法刪除。', 'error');
      } else {
        showToast('刪除失敗，請稍後再試。', 'error');
      }
      console.error('Failed to delete group:', error);
    }
  };

  const currentGroup = groups.find((g) => g.id === currentGroupId);

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="outline"
          onClick={() => setActualOpen(true)}
          className="gap-2 px-3 h-10 rounded-xl border-border hover:bg-muted"
        >
          <Users size={16} />
          <span className="text-sm font-medium">{currentGroup?.name || triggerLabel}</span>
          <ChevronDown size={16} className="ml-1 opacity-50" />
        </Button>
      )}

      <Dialog open={actualOpen} onOpenChange={setActualOpen}>
        <DialogContent className="bg-muted/30 border-border/50 rounded-[2rem] shadow-wash backdrop-blur-xl border border-white/5 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              群組管理
              {isSystemAdmin && (
                <span className="ml-2 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600">
                  系統管理員
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
              <AnimatePresence>
                {groups.map((group) => (
                  <motion.div
                    key={group.id}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className={`rounded-xl border transition-all ${
                      group.id === currentGroupId
                        ? 'bg-primary/10 border-primary/50'
                        : 'bg-muted/50 border-border hover:bg-muted/70'
                    }`}
                  >
                    {editingId === group.id ? (
                      <div className="p-3 space-y-3">
                        <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="群組名稱" className="h-9 rounded-lg" />
                        <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="描述（可選）" className="h-9 rounded-lg" />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateGroup(group.id)} className="flex-1 h-8 rounded-lg text-xs">保存</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="flex-1 h-8 rounded-lg text-xs">取消</Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="p-3 flex items-center justify-between">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={async () => {
                              await onGroupChange?.(group.id);
                              setActualOpen(false);
                            }}
                          >
                            <div className="flex items-center gap-1.5">
                              <h3 className="font-bold text-sm">{group.name}</h3>
                              {!!group.isPersonal && (
                                <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-600">個人</span>
                              )}
                            </div>
                            {group.description && <p className="text-xs text-muted-foreground mt-0.5">{group.description}</p>}
                            {group.memberCount !== undefined && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">{group.memberCount} 位成員</p>
                            )}
                          </div>
                          <div className="flex gap-1 items-center">
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => handleToggleMembers(group.id)}
                              className="h-8 w-8 p-0 rounded-lg"
                              title="查看成員"
                            >
                              {loadingMembersId === group.id
                                ? <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                                : <Mail size={13} />
                              }
                            </Button>
                            {!group.isPersonal && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => { setEditingId(group.id); setEditName(group.name); setEditDesc(group.description || ''); }}
                                className="h-8 w-8 p-0 rounded-lg"
                              >
                                <Edit2 size={13} />
                              </Button>
                            )}
                            {group.id !== 'default_group' && !group.isPersonal && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => handleDeleteGroup(group.id)}
                                className="h-8 w-8 p-0 rounded-lg hover:bg-destructive/20 hover:text-destructive"
                              >
                                <Trash2 size={13} />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Member list (expandable) */}
                        <AnimatePresence>
                          {expandedGroupId === group.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="px-3 pb-3 pt-1 space-y-1 border-t border-border/40">
                                {(groupMembersMap[group.id] ?? []).length === 0 ? (
                                  <p className="text-xs text-muted-foreground py-1">尚無成員資料</p>
                                ) : (
                                  (groupMembersMap[group.id] ?? []).map((m) => (
                                    <div key={m.id} className="flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-black text-primary flex-shrink-0">
                                        {m.nickname?.[0] || '?'}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold truncate">{m.nickname}</p>
                                        {m.email && <p className="text-[10px] text-muted-foreground truncate">{m.email}</p>}
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        {m.role === 'admin' && (
                                          <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600 flex items-center gap-0.5">
                                            <Shield size={8} /> 管理員
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>

              {!showCreateForm && (
                <Button
                  variant="ghost"
                  onClick={() => setShowCreateForm(true)}
                  className="w-full h-10 rounded-xl border border-dashed border-border hover:bg-muted font-medium text-sm"
                >
                  <Plus size={16} className="mr-2" />
                  建立新群組
                </Button>
              )}

              {showCreateForm && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-muted/50 border border-border space-y-3"
                >
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest ml-1 mb-2 block">群組名稱</Label>
                    <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="例如：室友" className="h-9 rounded-lg" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest ml-1 mb-2 block">描述（可選）</Label>
                    <Input value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)} placeholder="此群組的用途描述" className="h-9 rounded-lg" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleCreateGroup} className="flex-1 h-9 rounded-lg text-sm font-bold" disabled={!newGroupName.trim()}>建立群組</Button>
                    <Button variant="ghost" onClick={() => setShowCreateForm(false)} className="flex-1 h-9 rounded-lg text-sm">取消</Button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
